import { saveAs } from 'file-saver';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import API_BASE_URL, { APP_BASE_PATH, USER_MODULE } from '../../config';
import { useAuth } from '../../hooks/useAuth';
import { SearchResult, SourceReference, SummaryModelConfig } from '../../types/api';
import type { AssistantSearchSettings } from '../../utils/assistantStream';
import {
  buildExportFilename,
  exportResultsToDocxBlob,
  ExportOptions,
  ReferenceListLayout,
} from '../../utils/exportResultsToDocx';
import { withDocumentYears } from '../../utils/briefExportYears';
import { BriefReference, ReferenceGrouping } from './briefTypes';
import { BriefCentral } from './BriefCentral';
import { BriefRegenAllModal, NewBriefSubmit } from './BriefCentralModals';
import { BriefShareModal } from './BriefShareDialog';
import {
  BriefTemplateModal,
  TemplateDraft,
  draftHeading,
  draftToPayload,
} from './BriefTemplateModal';
import { BriefDocument } from './BriefDocument';
import { BriefComments, BriefCommentComposer, BriefThreadModal } from './BriefComments';
import { CommentMark } from './briefCommentMarks';
import {
  BriefSelection,
  BriefSelectionHighlight,
  BriefSelectionMenu,
} from './BriefSelectionMenu';
import { useBriefAnnotations, UseBriefAnnotationsReturn } from './useBriefAnnotations';
import { useBriefComments, UseBriefCommentsReturn } from './useBriefComments';
import { BriefHistoryModal } from './BriefHistoryModal';
import { BriefHistoryRail } from './BriefHistoryRail';
import { BriefToc } from './BriefToc';
import { IconArrowLeft } from './BriefIcons';
import { BriefGeneratingPanel, BriefSeed } from './BriefSeed';
import { DEFAULT_BRIEF_TITLE } from './briefTypes';
import { useBrief, UseBriefReturn } from './useBrief';
import { useBriefCentral } from './useBriefCentral';
import './brief.css';

// /brief/<server-uuid> deep links (share URLs). Base-path aware.
const briefPath = (id: string | null): string => {
  const suffix = id ? `/brief/${id}` : '/brief';
  return `${APP_BASE_PATH || ''}${suffix}`;
};

const briefIdFromLocation = (): string | null => {
  const path = window.location.pathname;
  const match = path.match(/\/brief\/([0-9a-f-]{36})\/?$/i);
  return match ? match[1] : null;
};

export const sourceToResult = (src: SourceReference, dataSource: string): SearchResult => ({
  chunk_id: src.chunkId,
  doc_id: src.docId,
  title: src.title,
  document_title: src.title,
  text: src.text,
  page_num: src.page || 1,
  score: src.score,
  headings: src.headings || [],
  data_source: dataSource,
  // Link every citation to the document's public "Report URL" (docs metadata)
  // so readers outside Evidence Lab can open it in a browser. resolveResultLink
  // uses report_url only after pdf_url, so we deliberately leave pdf_url unset
  // and fall back to the PDF URL when a source has no report URL.
  report_url: src.reportUrl ?? src.pdfUrl,
  metadata: {},
});

/**
 * Flatten the brief into one Markdown body + a SearchResult[] so it can reuse
 * the search summary's .docx exporter. The body is each section's display
 * text and the results are the References list, both exactly as the API
 * returned them, so the Word file carries the numbers readers see on screen.
 */
// Shown as a call-out box on the exported Word cover so readers treat the
// AI-generated content as a draft to verify, not a finished product.
const BRIEF_DISCLAIMER =
  'This content was auto-generated by AI by deep researching the documents library. ' +
  'Please verify any factual claims closely, and review for coverage and accuracy. ' +
  'This is meant as a guide for humans as part of the writing process, not as a final product.';

// The Word export's references layout for each on-screen grouping.
const REFERENCE_LIST_LAYOUT: Record<ReferenceGrouping, ReferenceListLayout> = {
  passage: 'flat',
  'document-multiple': 'grouped',
  'document-single': 'document',
};

// The source record behind a reference, for the export's result list. A
// reference always names its document; the passage record is what the API
// attached to it.
const referenceSource = (r: BriefReference): SourceReference =>
  r.source ?? { chunkId: '', docId: r.docId, title: r.title, text: '', score: 0, page: r.page ?? undefined };

export const assembleBriefForExport = (
  brief: Pick<ReturnType<typeof useBrief>, 'sections' | 'numbers' | 'numberHeadings' | 'references'>,
  dataSource: string,
): { summary: string; results: SearchResult[] } => {
  const lines: string[] = [];
  brief.sections.forEach((s, i) => {
    // The summary section's own H1 is the brief topic; sections sit one level
    // below it. The exporter demotes markdown headings by one, so `#` → H2 and
    // `##` → H3 — keeping sections in the Table of Contents (levels 1–2).
    const hashes = s.level === 2 ? '##' : '#';
    const prefix = brief.numberHeadings ? `${brief.numbers[i]}. ` : '';
    lines.push(`${hashes} ${prefix}${s.title}`, '');
    const shown = (s.status === 'done' || s.revising) && s.content;
    if (shown) lines.push(s.content, '');
  });
  // References are ordered by number, so results[n-1] is the source for `[n]`.
  const results = brief.references.map((r) => sourceToResult(referenceSource(r), dataSource));
  return { summary: lines.join('\n'), results };
};

// The open-brief view: back button (logged in), history rail (owners) and the
// document itself. Extracted from BriefTab to keep each component simple.
const BriefWorkspace: React.FC<{
  brief: ReturnType<typeof useBrief>;
  loggedIn: boolean;
  onBack: () => void;
  onResultClick?: (result: SearchResult) => void;
  onExportWord: () => void;
  exportBusy: boolean;
  onOpenModal: (modal: 'share' | 'template' | 'regen-all') => void;
  comments?: UseBriefCommentsReturn | null;
  activeThreadId?: string | null;
  onSelectThread?: (id: string | null) => void;
  onSelectText?: (selection: BriefSelection) => void;
  commentMarks?: Map<string, CommentMark[]>;
  onOpenThread?: (threadId: string) => void;
  showComments?: boolean;
  onToggleComments?: (show: boolean) => void;
  orphanedThreadIds?: string[];
}> = ({
  brief,
  loggedIn,
  onBack,
  onResultClick,
  onExportWord,
  exportBusy,
  onOpenModal,
  comments,
  activeThreadId = null,
  onSelectThread = () => {},
  onSelectText,
  commentMarks,
  onOpenThread,
  showComments,
  onToggleComments,
  orphanedThreadIds,
}) => {
  // Logged-in layout (Brief Central): the side rail is the sticky Contents
  // panel — history lives on the landing page. Anonymous layout keeps the
  // saved-briefs history rail and the inline Contents panel.
  const canEditStructure =
    brief.canEdit && !brief.sections.some((s) => s.status === 'researching');
  // Offset the sticky rail below the app's sticky top bar so the first
  // headings never slide underneath it.
  const [railTop, setRailTop] = useState(88);
  useEffect(() => {
    const measure = () => {
      const bar = document.querySelector('.top-bar');
      setRailTop((bar instanceof HTMLElement ? bar.offsetHeight : 72) + 16);
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, []);
  return (
    <>
      {brief.error && <div className="brief-error brief-error-banner">{brief.error}</div>}
      {loggedIn && (
        <div className="brief-back-row">
          <button className="brief-back-btn" onClick={onBack}>
            <IconArrowLeft /> Brief Central
          </button>
        </div>
      )}
      <div className="brief-builder">
        {loggedIn ? (
          <aside
            className="brief-toc-rail"
            style={{ top: railTop, maxHeight: `calc(100vh - ${railTop + 16}px)` }}
          >
            <BriefToc brief={brief} canEdit={canEditStructure} />
          </aside>
        ) : (
          <BriefHistoryRail brief={brief} />
        )}
        <BriefDocument
          brief={brief}
          onResultClick={onResultClick}
          onExportWord={onExportWord}
          exportBusy={exportBusy}
          onOpenShare={
            loggedIn && brief.currentBriefId ? () => onOpenModal('share') : undefined
          }
          onSaveTemplate={loggedIn ? () => onOpenModal('template') : undefined}
          onRegenerateAll={() => onOpenModal('regen-all')}
          showToc={!loggedIn}
          onSelectText={comments ? onSelectText : undefined}
          commentMarks={commentMarks}
          onOpenThread={onOpenThread}
          activeThreadId={activeThreadId}
          showComments={showComments}
          onToggleComments={onToggleComments}
        />
        {comments && showComments !== false && (
          <BriefComments
            orphanedThreadIds={orphanedThreadIds}
            comments={comments}
            canResolve={brief.canEdit}
            activeThreadId={activeThreadId}
            onSelectThread={onSelectThread}
          />
        )}
      </div>
    </>
  );
};

interface BriefTabProps {
  dataSource: string;
  // The configured chat / deep-research model, used for outline + section research.
  assistantModelConfig?: SummaryModelConfig | null;
  // The app's active reranker + search settings (group-seeded for logged-in
  // users). Applied to brief searches only when logged in.
  rerankerModel?: string | null;
  searchSettings?: AssistantSearchSettings | null;
  // Model for the LLM semantic highlighter (combo.semantic_highlighting_model),
  // used to mark claim-supporting spans in citation excerpts.
  semanticModelConfig?: SummaryModelConfig | null;
  // Opens the document preview (citations/footnotes click through to it).
  onResultClick?: (result: SearchResult) => void;
}

// The annotation layer over an open brief: the marked passage, the selection
// toolbar, a thread opened from the text, and the comment being written.
const BriefAnnotationLayer: React.FC<{
  annotations: UseBriefAnnotationsReturn;
  comments: UseBriefCommentsReturn | null;
  canResolve: boolean;
}> = ({ annotations, comments, canResolve }) => {
  const {
    selection,
    setSelection,
    selectionActions,
    pendingComment,
    clearPendingComment,
    threadModalId,
    closeThreadModal,
  } = annotations;
  const marked = selection || pendingComment;
  return (
    <>
      {marked && <BriefSelectionHighlight rects={marked.rects} />}
      {threadModalId && comments && (
        <BriefThreadModal
          threadId={threadModalId}
          comments={comments}
          canResolve={canResolve}
          onClose={closeThreadModal}
        />
      )}
      {selection && (
        <BriefSelectionMenu
          selection={selection}
          actions={selectionActions}
          onClose={() => setSelection(null)}
        />
      )}
      {pendingComment && comments && (
        <BriefCommentComposer
          quote={pendingComment.quote}
          onCancel={clearPendingComment}
          onSubmit={(body) => {
            void comments.add({
              body,
              sectionId: pendingComment.sectionId,
              quote: pendingComment.quote,
              quotePrefix: pendingComment.prefix,
              quoteSuffix: pendingComment.suffix,
            });
            clearPendingComment();
          }}
        />
      )}
    </>
  );
};

/** The Word export of a brief: its prose, references laid out per the
 *  reader's grouping, and links pointing where the reader chose (each
 *  document's source, or its copy in Evidence Lab). */
export const briefWordExportOptions = (
  brief: Pick<UseBriefReturn, 'briefTitle' | 'referenceGrouping' | 'wordLinkTarget'>,
  summary: string,
  results: SearchResult[],
  dataSource: string,
): ExportOptions => ({
  query: brief.briefTitle || DEFAULT_BRIEF_TITLE,
  aiSummary: summary,
  results,
  dataSource,
  documentTitle: 'AI-generated Research Brief',
  summaryHeading: brief.briefTitle || DEFAULT_BRIEF_TITLE,
  infoBox: BRIEF_DISCLAIMER,
  tableOfContents: true,
  resultsSectionTitle: 'References',
  // The document mirrors the on-screen References section: inline [n]
  // citations and a compact list laid out per the reader's grouping.
  citationStyle: 'links',
  referenceList: REFERENCE_LIST_LAYOUT[brief.referenceGrouping],
  linkTarget: brief.wordLinkTarget,
  apiBaseUrl: API_BASE_URL,
  siteOrigin: typeof window !== 'undefined' && window.location ? window.location.origin : undefined,
  // Same API base the on-screen cards use to load table/figure screenshots,
  // so the brief embeds those exact images.
  fileBaseUrl: API_BASE_URL,
});

export const BriefTab: React.FC<BriefTabProps> = ({
  dataSource,
  assistantModelConfig,
  rerankerModel,
  searchSettings,
  semanticModelConfig,
  onResultClick,
}) => {
  const auth = useAuth();
  // Logged-in users get their own saved-briefs bucket; anonymous users share one.
  const userKey = USER_MODULE && auth.user ? String(auth.user.id) : null;
  const loggedIn = userKey != null;
  // Logged-in users get Brief Central: server-side briefs, sharing, templates
  // and voice & tone profiles. Anonymous users keep the localStorage flow.
  // Administrators (superusers) also get the All Briefs tab.
  const isAdmin = loggedIn && !!auth.user?.is_superuser;
  const central = useBriefCentral(loggedIn, isAdmin);
  const brief = useBrief({
    apiBaseUrl: API_BASE_URL,
    dataSource,
    assistantModelConfig,
    // Use the group's search settings only when logged in; anonymous briefs run
    // without reranking (the default cross-encoder is too slow for many queries).
    rerankerModel: loggedIn ? rerankerModel ?? null : null,
    searchSettings: loggedIn ? searchSettings ?? null : null,
    userKey,
    remote: loggedIn,
    voices: central.voices,
    semanticModelConfig,
  });
  const [exportBusy, setExportBusy] = useState(false);
  // Comments on the open brief, plus the selection awaiting a comment body.
  const comments = useBriefComments(brief.currentBriefId, loggedIn);
  const annotations = useBriefAnnotations(brief.sections, comments);
  const {
    showComments,
    toggleComments,
    commentMarks,
    orphanedThreadIds,
    activeThreadId,
    threadModalId,
    closeThreadModal,
    openThreadFromText,
    jumpToThreadPassage,
    selection,
    setSelection,
    selectionActions,
    pendingComment,
    clearPendingComment,
  } = annotations;

  const [workspaceModal, setWorkspaceModal] = useState<
    'share' | 'template' | 'regen-all' | null
  >(null);

  // Deep link: open /brief/<id> (a share URL) once auth has resolved.
  const { openBriefById } = brief;
  useEffect(() => {
    if (!loggedIn) return;
    const id = briefIdFromLocation();
    if (id) openBriefById(id);
  }, [loggedIn, openBriefById]);

  const openBrief = useCallback(
    (id: string) => {
      brief.openBriefById(id);
      window.history.pushState(null, '', briefPath(id));
    },
    [brief],
  );

  // Keep the URL canonical: App's tab navigation pushes a bare /brief, which
  // would strip the open brief's id (and break copy-from-address-bar links).
  // Restore it whenever the workspace has a brief open. Runs on every render —
  // pathname isn't reactive state — and is an idempotent replaceState.
  useEffect(() => {
    if (!loggedIn || !brief.currentBriefId || brief.stage === 'seed') return;
    const want = briefPath(brief.currentBriefId);
    if (window.location.pathname !== want && briefIdFromLocation() === null) {
      window.history.replaceState(null, '', want);
    }
  });

  const backToCentral = useCallback(() => {
    brief.reset();
    window.history.pushState(null, '', briefPath(null));
    void central.refresh();
  }, [brief, central]);

  const createBrief = useCallback(
    (args: NewBriefSubmit) => {
      brief.setQuery(args.title);
      brief.setInstructions(args.instructions);
      brief.setNumHeadings(args.numHeadings);
      brief.setBriefVoiceId(args.voiceId);
      brief.setTargetWords(args.targetWords);
      if (args.mode === 'ai') {
        void brief.generateOutline({
          topic: args.title,
          instructions: args.instructions,
          numHeadings: args.numHeadings,
        });
        return;
      }
      brief.startFromTemplate(
        args.title || args.template?.name || DEFAULT_BRIEF_TITLE,
        args.template ? args.template.headings : [],
      );
    },
    [brief],
  );

  const handleExportWord = useCallback(
    async () => {
      if (exportBusy) return;
      setExportBusy(true);
      try {
        const { summary, results } = assembleBriefForExport(brief, dataSource);
        // The exported References list shows each document's year after its
        // title. Brief sources don't carry it, so look it up per cited document
        // in the data source the brief was researched in (a saved brief records
        // it; a new brief's sources are in the app's selected data source).
        const blob = await exportResultsToDocxBlob(
          briefWordExportOptions(brief, summary, await withDocumentYears(results, brief.briefDataSource ?? dataSource), dataSource),
        );
        saveAs(blob, buildExportFilename(brief.briefTitle || 'evidence-brief', new Date()));
      } catch (err) {
        brief.setError(err instanceof Error ? err.message : 'Export to Word failed');
      } finally {
        setExportBusy(false);
      }
    },
    [exportBusy, brief, dataSource],
  );

  // The template draft when saving the open brief as a template: its headings
  // with each section's text, prompt, voice and length, and the brief's own
  // prompt, voice and length. The editor's switches decide what is kept.
  const templateFromBrief: TemplateDraft = {
    id: null,
    fromBrief: true,
    name: `${brief.briefTitle.slice(0, 40)} template`,
    description: 'Saved from a brief',
    headings: brief.sections.map((s) =>
      draftHeading({
        title: s.title,
        sub: s.level === 2,
        text: s.content || null,
        prompt: s.guidance || null,
        voice_profile_id: s.voiceId ?? null,
        target_words: s.targetWords ?? null,
      }),
    ),
    withText: false,
    withSettings: true,
    prompt: brief.instructions,
    voiceId: brief.briefVoiceId,
    targetWords: brief.targetWords,
  };

  // Logged-in outline generation runs from the New-brief modal — surface the
  // live research activity instead of silently sitting on the landing page.
  const landing = loggedIn ? (
    brief.outlineLoading ? (
      <>
        {brief.error && <div className="brief-error brief-error-banner">{brief.error}</div>}
        <BriefGeneratingPanel brief={brief} />
      </>
    ) : (
      <>
        {brief.error && <div className="brief-error brief-error-banner">{brief.error}</div>}
        <BriefCentral
          central={central}
          onOpenBrief={openBrief}
          onCreateBrief={createBrief}
          defaultTargetWords={brief.targetWords}
        />
      </>
    )
  ) : (
    <BriefSeed brief={brief} />
  );

  return (
    <div className={`brief-tab${brief.stage === 'seed' ? '' : ' brief-tab-full'}`}>
      {brief.stage === 'seed' ? (
        landing
      ) : (
        <BriefWorkspace
          brief={brief}
          loggedIn={loggedIn}
          onBack={backToCentral}
          onResultClick={onResultClick}
          onExportWord={handleExportWord}
          exportBusy={exportBusy}
          onOpenModal={setWorkspaceModal}
          comments={loggedIn && brief.currentBriefId ? comments : null}
          activeThreadId={activeThreadId}
          onSelectThread={jumpToThreadPassage}
          onSelectText={setSelection}
          commentMarks={commentMarks}
          onOpenThread={openThreadFromText}
          showComments={showComments}
          onToggleComments={toggleComments}
          orphanedThreadIds={orphanedThreadIds}
        />
      )}
      <BriefAnnotationLayer
        annotations={annotations}
        comments={comments}
        canResolve={brief.canEdit}
      />
      <BriefHistoryModal brief={brief} />
      {workspaceModal === 'share' && brief.currentBriefId && (
        <BriefShareModal
          briefId={brief.currentBriefId}
          briefTitle={brief.briefTitle}
          onChanged={() => void central.refresh()}
          onClose={() => setWorkspaceModal(null)}
        />
      )}
      {workspaceModal === 'template' && (
        <BriefTemplateModal
          draft={templateFromBrief}
          voices={brief.voices}
          onSave={async (d) => {
            await central.saveTemplate(null, draftToPayload(d));
            setWorkspaceModal(null);
          }}
          onClose={() => setWorkspaceModal(null)}
        />
      )}
      {workspaceModal === 'regen-all' && (
        <BriefRegenAllModal
          voices={brief.voices}
          briefVoiceId={brief.briefVoiceId}
          instructions={brief.instructions}
          targetWords={brief.targetWords}
          hasSectionVoices={brief.sections.some((s) => !!s.voiceId)}
          onSubmit={({ instructions, voiceId, targetWords, applyVoiceToAllSections }) => {
            if (applyVoiceToAllSections) {
              brief.sections.forEach((s) => {
                if (s.voiceId) brief.setSectionVoiceId(s.id, null);
              });
            }
            setWorkspaceModal(null);
            // Passed explicitly: the research loop reads refs, which React
            // would not have updated from the setters by the time it starts.
            void brief.startResearch({ instructions, voiceId, targetWords });
          }}
          onClose={() => setWorkspaceModal(null)}
        />
      )}
    </div>
  );
};
